import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { PushToggle } from "@/components/PushToggle";
import { PasskeySection } from "@/components/Passkeys";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, fmtDT, useApi, mobile10 } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ForgotPassword } from "@/pages/Auth";

function PasswordSection() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <section className="surface p-6" data-testid="password-section">
      <h3 className="font-semibold">Password</h3>
      <p className="text-sm muted mt-1">{user?.has_password ? "You can sign in with your password or an email code." : "Set a password to sign in without waiting for an email code."}</p>
      {open ? <div className="mt-4 max-w-sm"><ForgotPassword onBack={() => setOpen(false)} /></div> : <Button variant="outline" className="mt-4" onClick={() => setOpen(true)} data-testid="set-password-btn">{user?.has_password ? "Change password" : "Set password"}</Button>}
    </section>
  );
}

export function PhoneSection({ onChanged }) {
  const { user, setUser } = useAuth();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const request = async () => { try { await api.post("/auth/phone/change/request", { phone }); setSent(true); toast.success("Code sent by SMS"); } catch (e) { toast.error(errMsg(e)); } };
  const verify = async () => {
    try { const { data } = await api.post("/auth/phone/change/verify", { phone, code }); setUser({ ...user, phone: data.phone }); setSent(false); setPhone(""); setCode(""); toast.success("Mobile number updated"); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="surface p-6" data-testid="phone-section">
      <h3 className="font-semibold">Mobile number</h3>
      <p className="text-sm muted mt-1" data-testid="phone-current">Current: {user?.phone ? `+91 ${user.phone}` : "not set"}. You can sign in with this number, and updates are sent to it by SMS. Changing it requires an SMS code.</p>
      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <Input inputMode="tel" value={phone} onChange={(e) => { setPhone(mobile10(e.target.value)); setSent(false); }} placeholder="New 10-digit mobile" data-testid="phone-change-input" />
        {!sent ? <Button variant="outline" onClick={request} disabled={phone.length !== 10} data-testid="phone-change-request-btn">Send code</Button> : (
          <><Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="6-digit code" className="sm:w-40" data-testid="phone-change-code-input" />
            <Button onClick={verify} disabled={code.length !== 6} data-testid="phone-change-verify-btn">Verify</Button></>)}
      </div>
    </section>
  );
}

export function MarketingPrefs() {
  const { user, setUser } = useAuth();
  const toggle = async (v) => {
    try { const { data } = await api.put("/auth/me/preferences", { marketing_sms_opt_out: !v }); setUser({ ...user, ...data }); toast.success(v ? "You'll receive offers by SMS" : "Promotional SMS turned off"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="surface p-6" data-testid="marketing-prefs-section">
      <h3 className="font-semibold">SMS preferences</h3>
      <p className="text-sm muted mt-1">Codes, appointment and order updates are always sent by SMS to your registered mobile. You can choose whether to receive offers and health programme news.</p>
      <label className="mt-4 flex items-center gap-3 text-sm"><Switch checked={!user?.marketing_sms_opt_out} onCheckedChange={toggle} data-testid="marketing-sms-toggle" />Send me offers & health programme updates by SMS</label>
    </section>
  );
}

export default function AccountSettings() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const sessions = useApi("/auth/sessions");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [open, setOpen] = useState(false);

  const requestChange = async () => {
    try { await api.post("/auth/email-change/request", { new_email: email }); setSent(true); toast.success("Code sent to the new email"); } catch (e) { toast.error(errMsg(e)); }
  };
  const verifyChange = async () => {
    try { const { data } = await api.post("/auth/email-change/verify", { new_email: email, code }); setUser({ ...user, email: data.email }); setSent(false); setEmail(""); setCode(""); toast.success("Email updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async () => { try { const { data } = await api.post("/auth/sessions/revoke-others"); toast.success(`Signed out ${data.revoked} other session(s)`); sessions.reload(); } catch (e) { toast.error(errMsg(e)); } };
  const deactivate = async () => {
    try { await api.post("/auth/deactivate", { confirm }); setUser(false); nav("/"); toast.success("Account deactivated"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6" data-testid="account-settings">
      <PasswordSection />
      {user?.role !== "admin" && <><PhoneSection /><MarketingPrefs /></>}
      {["patient", "doctor"].includes(user?.role) && <PushToggle />}
      {user?.role === "patient" && <PasskeySection />}
      <section className="surface p-6">
        <h3 className="font-semibold">Sign-in email</h3>
        <p className="text-sm muted mt-1">Current: {user?.email}. Changing it requires verifying a code sent to the new address.</p>
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="new@email.com" data-testid="email-change-input" />
          {!sent ? <Button variant="outline" onClick={requestChange} disabled={!email} data-testid="email-change-request-btn">Send code</Button> : (
            <>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" className="sm:w-40" data-testid="email-change-code-input" />
              <Button onClick={verifyChange} data-testid="email-change-verify-btn">Verify</Button>
            </>
          )}
        </div>
      </section>
      <section className="surface p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Active sessions</h3>
          <Button variant="outline" size="sm" onClick={revoke} data-testid="revoke-sessions-btn">Sign out other devices</Button>
        </div>
        <div className="mt-3 divide-y divide-[#E2E8ED]">
          {(sessions.data || []).map((s) => (
            <div key={s.id} className="py-2 text-sm flex justify-between gap-3"><span className="truncate muted">{s.user_agent?.slice(0, 60) || "Device"}{s.remember && <span className="ml-2 text-[11px] text-[#086B2D]" data-testid={`session-remembered-${s.id}`}>· remembered until {fmtDT(s.expires_at)}</span>}</span><span className="shrink-0">{s.current ? "This device" : fmtDT(s.created_at)}</span></div>
          ))}
        </div>
      </section>
      {user?.role !== "admin" && (
        <section className="surface p-6 border-red-200">
          <h3 className="font-semibold text-red-700">Deactivate account</h3>
          <p className="text-sm muted mt-1">Your clinical records are retained as required by law. Contact support to recover a deactivated account.</p>
          <Button variant="outline" className="mt-4 border-red-300 text-red-700 hover:bg-red-50" onClick={() => setOpen(true)} data-testid="deactivate-open-btn">Deactivate</Button>
        </section>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Deactivate your account?</DialogTitle><DialogDescription>Type DEACTIVATE to confirm. You will be signed out on all devices.</DialogDescription></DialogHeader>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="deactivate-confirm-input" />
          <DialogFooter><Button variant="destructive" onClick={deactivate} data-testid="deactivate-confirm-btn">Deactivate</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
